// src/pages/ReaderPage.jsx
// Immersive, distraction-free PDF reading room.
// Default: single-page scroll. Optional: two-page book spread on wide screens.
// Uses Mozilla PDF.js loaded from CDN. Progress auto-saved to localStorage.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import useSEO from "../hooks/useSEO";
import { recordPageView } from "../utils/gamification";
import { getAuthToken } from "../utils/authToken";

import useReaderTTS from "../hooks/useReaderTTS";
import { useAuth } from "../context/AuthContext";

import ReaderToolbar from "../components/reader/ReaderToolbar";
import AudioPlayerWidget from "../components/reader/AudioPlayerWidget";
import BadgeUnlockModal from "../components/reader/BadgeUnlockModal";

// ─────────────────────────────────────────────────────────────────────────────
// Render a single PDF page onto a canvas element.
// Returns the render task so it can be cancelled.
// ─────────────────────────────────────────────────────────────────────────────
function startRender(pdfDoc, pageNum, zoom, canvas, textLayerDiv) {
  if (!pdfDoc || !canvas) return null;
  if (pageNum < 1 || pageNum > pdfDoc.numPages) return null;

  const dpr = window.devicePixelRatio || 1;

  let renderTask = null;

  const promise = pdfDoc.getPage(pageNum).then((page) => {
    const viewport = page.getViewport({ scale: zoom });
    canvas.width  = Math.floor(viewport.width  * dpr);
    canvas.height = Math.floor(viewport.height * dpr);
    canvas.style.width  = `${Math.floor(viewport.width)}px`;
    canvas.style.height = `${Math.floor(viewport.height)}px`;

    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    renderTask = page.render({ canvasContext: ctx, viewport });

    if (textLayerDiv && window.pdfjsLib) {
      textLayerDiv.innerHTML = ""; // clear previous
      textLayerDiv.style.width = `${Math.floor(viewport.width)}px`;
      textLayerDiv.style.height = `${Math.floor(viewport.height)}px`;
      textLayerDiv.style.setProperty("--scale-factor", zoom);

      page.getTextContent().then((textContent) => {
        if (typeof window.pdfjsLib.renderTextLayer === "function") {
          window.pdfjsLib.renderTextLayer({
            textContent: textContent,
            textContentSource: textContent,
            container: textLayerDiv,
            viewport: viewport,
            enhanceTextSelection: true,
          });
        }
      }).catch(err => {
        console.warn("[Reader] Text layer render failed:", err);
      });
    }

    return renderTask.promise;
  });

  return { promise, cancel: () => renderTask?.cancel() };
}

function normalizeAnnotationText(value) {
  return (value || "").replace(/\s+/g, " ").trim().toLowerCase();
}

function findMatchingSpanIndexes(spans, selectedText, preferredStart) {
  const target = normalizeAnnotationText(selectedText);
  if (!target) return [];

  const starts = Number.isInteger(preferredStart)
    ? [preferredStart, ...spans.map((_, index) => index).filter((index) => index !== preferredStart)]
    : spans.map((_, index) => index);

  for (const start of starts) {
    let combined = "";
    const indexes = [];

    for (let i = start; i < spans.length; i++) {
      const text = normalizeAnnotationText(spans[i].textContent);
      if (!text) continue;

      combined = combined ? `${combined} ${text}` : text;
      indexes.push(i);

      if (combined.includes(target)) return indexes;
      if (combined.length > target.length + 120) break;
    }
  }

  return spans
    .map((span, index) => ({ span, index }))
    .filter(({ span }) => {
      const text = normalizeAnnotationText(span.textContent);
      return text && (target.includes(text) || text.includes(target));
    })
    .slice(0, 8)
    .map(({ index }) => index);
}

function selectionBtnStyle(primary = false) {
  return {
    border: "none",
    borderRadius: "999px",
    minWidth: "76px",
    height: "30px",
    padding: "0 12px",
    background: primary ? "#C8922A" : "rgba(255,255,255,0.1)",
    color: primary ? "#1A1208" : "#fff",
    cursor: "pointer",
    fontFamily: "'Outfit', sans-serif",
    fontSize: "0.76rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
  };
}

function panelBtnStyle(S, primary = false) {
  return {
    border: `1px solid ${primary ? S.accent : S.panelBorder}`,
    borderRadius: "7px",
    background: primary ? S.accent : "transparent",
    color: primary ? "#fff" : S.text,
    cursor: "pointer",
    fontFamily: "'Outfit', sans-serif",
    fontSize: "0.78rem",
    fontWeight: 700,
    padding: "7px 12px",
  };
}

// Simple inline markdown formatter to support standard theological assistant responses without dependencies
function renderMarkdown(text) {
  if (!text) return "";
  const lines = text.split("\n");
  return lines.map((line, idx) => {
    let content = line.trim();
    if (!content) return <div key={idx} style={{ height: "0.5rem" }} />;

    // Headers
    if (content.startsWith("### ")) {
      return (
        <h3 key={idx} style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: "1.1rem",
          fontWeight: 700,
          margin: "12px 0 6px 0",
          color: "var(--ink)",
        }}>
          {content.slice(4)}
        </h3>
      );
    }
    if (content.startsWith("#### ")) {
      return (
        <h4 key={idx} style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: "0.85rem",
          fontWeight: 700,
          margin: "10px 0 4px 0",
          color: "var(--ink)",
          textTransform: "uppercase",
          letterSpacing: "0.02em",
        }}>
          {content.slice(5)}
        </h4>
      );
    }

    // Bullets
    if (content.startsWith("* ") || content.startsWith("- ")) {
      const parts = content.slice(2).split("**");
      return (
        <li key={idx} style={{
          fontSize: "0.86rem",
          lineHeight: 1.5,
          marginLeft: "12px",
          marginBottom: "4px",
          color: "var(--ink)",
        }}>
          {parts.map((part, pIdx) => 
            pIdx % 2 === 1 ? <strong key={pIdx} style={{ color: "var(--gold)" }}>{part}</strong> : part
          )}
        </li>
      );
    }

    // Normal paragraph with bold highlights
    const parts = content.split("**");
    return (
      <p key={idx} style={{
        fontSize: "0.86rem",
        lineHeight: 1.5,
        marginBottom: "8px",
        color: "var(--ink)",
      }}>
        {parts.map((part, pIdx) => 
          pIdx % 2 === 1 ? <strong key={pIdx} style={{ color: "var(--gold)" }}>{part}</strong> : part
        )}
      </p>
    );
  });
}

export default function ReaderPage({ books, loading: booksLoading, theme = "light", setTheme }) {
  const { bookId } = useParams();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const format = searchParams.get("format") || "pdf";

  const book = books.find((b) => b.id === bookId);
  const { user, syncToCloud } = useAuth();

  // ── Core state ─────────────────────────────────────────────────────────────
  const [pdfLibLoaded, setPdfLibLoaded] = useState(false);
  const [loadingPdf,   setLoadingPdf]   = useState(true);
  const [loadError,    setLoadError]    = useState("");
  const [pdf,          setPdf]          = useState(null);
  const [pageNum,      setPageNum]      = useState(1);
  const [numPages,     setNumPages]     = useState(0);

  // EPUB reader state
  const [epubLibLoaded, setEpubLibLoaded] = useState(false);
  const [rendition, setRendition] = useState(null);
  const [epubText, setEpubText] = useState("");
  const epubViewerRef = useRef(null);
  const epubBookRef = useRef(null);
  const [zoom,         setZoom]         = useState(1.15);
  const [newBadge,     setNewBadge]     = useState(null);
  const [spreadMode,   setSpreadMode]   = useState(() => window.innerWidth > 1024);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [selectedTextInfo, setSelectedTextInfo] = useState(null);
  const [pendingNote, setPendingNote] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [annotations, setAnnotations] = useState([]);
  const [activeAnnotationId, setActiveAnnotationId] = useState(null);

  // Scholar AI drawer state
  const [showScholarDrawer, setShowScholarDrawer] = useState(false);
  const [scholarPassage, setScholarPassage] = useState(null);
  const [scholarHistory, setScholarHistory] = useState([]); // Array of { role, content }
  const [scholarLoading, setScholarLoading] = useState(false);
  const [scholarInput, setScholarInput] = useState("");
  const [scholarError, setScholarError] = useState("");

  // Canvas and Layer refs
  const canvasLeftRef  = useRef(null);
  const canvasRightRef = useRef(null);
  const textLayerLeftRef = useRef(null);
  const textLayerRightRef = useRef(null);

  // Active render tasks
  const renderLeftRef  = useRef(null);
  const renderRightRef = useRef(null);

  // Scroll container ref
  const scrollViewportRef = useRef(null);

  // Page jump input
  const pageInputRef = useRef(null);
  const rightPageNum = pageNum + 1;



  const {
    showAudioPlayer,
    setShowAudioPlayer,
    isSpeaking,
    voiceGender,
    setVoiceGender,
    selectedVoiceName,
    setSelectedVoiceName,
    voices,
    extractedText,
    loadingText,
    voicesLoaded,
    speechBoundary,
    playDisabled,
    voiceGroups,
    stopSpeech,
    startSpeech,
    toggleSpeech,
    stopSpeechOnNavigation
  } = useReaderTTS({ pdf, pageNum, numPages, spreadMode, setPageNum, customText: format === "epub" ? epubText : null });

  // ── Gamification: Record Page View & Check Badge Unlocks ──────────────────────
  const prevPageRef = useRef(null);
  const isFirstPageRecord = useRef(true);

  // Reset tracking state if book changes
  useEffect(() => {
    isFirstPageRecord.current = true;
    prevPageRef.current = null;
  }, [book?.id]);

  useEffect(() => {
    if (books && books.length > 0) {
      window.__EBENEZER_BOOKS_LIST__ = books;
    }
  }, [books]);

  useEffect(() => {
    if (loadingPdf || !book || !pageNum) return;

    if (isFirstPageRecord.current) {
      prevPageRef.current = pageNum;
      isFirstPageRecord.current = false;
      return;
    }

    if (prevPageRef.current !== pageNum) {
      const newlyUnlocked = recordPageView(book, prevPageRef.current, spreadMode, numPages);
      if (newlyUnlocked && newlyUnlocked.length > 0) {
        setNewBadge(newlyUnlocked[0]);
      }
      prevPageRef.current = pageNum;
    }
  }, [book, pageNum, spreadMode, numPages, loadingPdf]);

  // Auto-sync page progress in background (debounced by 3 seconds to avoid API spam)
  useEffect(() => {
    if (loadingPdf || !book || !pageNum || !user) return;

    const timer = setTimeout(() => {
      syncToCloud(user).catch((e) => console.error("[ReaderPage] Page progress auto-sync failed:", e));
    }, 3000);

    return () => clearTimeout(timer);
  }, [pageNum, book, loadingPdf, user, syncToCloud]);

  // Immediately sync progress when exiting the reader (unmount) or closing/minimizing the tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden" && user) {
        syncToCloud(user).catch((e) => console.error("[ReaderPage] Visibility sync failed:", e));
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (user) {
        syncToCloud(user).catch((e) => console.error("[ReaderPage] Unmount sync failed:", e));
      }
    };
  }, [user, syncToCloud]);

  // Reset side panels when switching books to prevent state leakage
  useEffect(() => {
    setShowScholarDrawer(false);
    setScholarPassage(null);
    setScholarHistory([]);
    setScholarInput("");
    setScholarError("");
    setShowNotesPanel(false);
  }, [bookId]);

  // ── SEO ────────────────────────────────────────────────────────────────────
  useSEO({
    title: book
      ? `Reading: ${book.title} — Ebenezer Books`
      : "Reader — Ebenezer Books",
    description: book
      ? `Read "${book.title}" by ${book.author} in our distraction-free web reader.`
      : "",
    ogType: "book",
  });

  // ── Themes ─────────────────────────────────────────────────────────────────
  const THEMES = {
    light: {
      bg:           "#FDFBF7",
      pageBg:       "#F5ECD7",
      panelBg:      "#FAF6ED",
      panelBorder:  "#E3D5BE",
      text:         "#1E160E",
      accent:       "#C8922A",
      shadow:       "rgba(30,22,14,0.14)",
      hover:        "rgba(200, 146, 42, 0.12)",
      highlightBg:  "rgba(235, 176, 64, 0.36)",
      highlightRing:"rgba(159, 105, 25, 0.72)",
      selectBg:     "rgba(235, 176, 64, 0.32)",
      savedHighlightBg: "rgba(235, 176, 64, 0.34)",
      savedHighlightRing: "rgba(159, 105, 25, 0.5)",
      noteBg:       "rgba(43, 111, 151, 0.24)",
      noteRing:     "rgba(32, 91, 126, 0.55)",
      noteText:     "#255D80",
      spineBg:      "linear-gradient(180deg,#D4B896 0%,#B8966A 50%,#D4B896 100%)",
      spineShine:   "rgba(255,240,200,0.45)",
      canvasFilter: "none",
    },
    dark: {
      bg:           "#0E1622",
      pageBg:       "#182535",
      panelBg:      "#121E2C",
      panelBorder:  "#24384F",
      text:         "#CBD5E1",
      accent:       "#3B82F6",
      shadow:       "rgba(0,0,0,0.5)",
      hover:        "rgba(59,130,246,0.12)",
      highlightBg:  "rgba(96, 165, 250, 0.34)",
      highlightRing:"rgba(147, 197, 253, 0.86)",
      selectBg:     "rgba(96, 165, 250, 0.34)",
      savedHighlightBg: "rgba(245, 188, 73, 0.34)",
      savedHighlightRing: "rgba(255, 214, 126, 0.74)",
      noteBg:       "rgba(79, 190, 183, 0.32)",
      noteRing:     "rgba(125, 229, 222, 0.76)",
      noteText:     "#9BEAE4",
      spineBg:      "linear-gradient(180deg,#24384F 0%,#121E2C 50%,#24384F 100%)",
      spineShine:   "rgba(59,130,246,0.15)",
      canvasFilter: "invert(0.9) hue-rotate(200deg) contrast(1.1) brightness(0.95)",
    },
  };

  const S = THEMES[theme] ?? THEMES.light;
  const annotationStorageKey = book ? `reader_annotations_${book.id}` : null;

  useEffect(() => {
    if (!annotationStorageKey) return;
    try {
      const saved = localStorage.getItem(annotationStorageKey);
      setAnnotations(saved ? JSON.parse(saved) : []);
    } catch (e) {
      console.warn("[Reader] Could not load annotations:", e);
      setAnnotations([]);
    }
  }, [annotationStorageKey]);

  const persistAnnotations = useCallback((updater) => {
    if (!annotationStorageKey) return;
    setAnnotations((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      localStorage.setItem(annotationStorageKey, JSON.stringify(next));
      localStorage.setItem("ebenezer_progress_last_updated", String(Date.now()));
      if (user) {
        syncToCloud(user).catch((e) => console.error("[ReaderPage] Background sync failed:", e));
      }
      return next;
    });
  }, [annotationStorageKey, user, syncToCloud]);

  const applyAnnotationHighlights = useCallback(() => {
    const layerPairs = [
      { page: pageNum, layer: textLayerLeftRef.current },
      { page: rightPageNum, layer: textLayerRightRef.current },
    ];

    layerPairs.forEach(({ layer }) => {
      layer?.querySelectorAll("span").forEach((span) => {
        span.dataset.annotationHighlight = "false";
        span.dataset.annotationId = "";
        span.style.background = "none";
        span.style.boxShadow = "none";
        span.style.borderRadius = "";
      });
    });

    layerPairs.forEach(({ page, layer }) => {
      if (!layer) return;
      const spans = Array.from(layer.querySelectorAll("span"));
      const pageAnnotations = annotations.filter((item) => item.page === page);

      pageAnnotations.forEach((item) => {
        const indexes = findMatchingSpanIndexes(spans, item.text, item.spanIndex);
        indexes.forEach((index) => {
          const span = spans[index];
          span.dataset.annotationHighlight = "true";
          span.dataset.annotationId = item.id;
          span.style.background = item.type === "note" ? S.noteBg : S.savedHighlightBg;
          span.style.boxShadow = item.id === activeAnnotationId
            ? `0 0 0 3px ${item.type === "note" ? S.noteRing : S.savedHighlightRing}, 0 0 18px ${item.type === "note" ? S.noteRing : S.savedHighlightRing}`
            : `0 0 0 1px ${item.type === "note" ? S.noteRing : S.savedHighlightRing}`;
          span.style.borderRadius = "4px";
        });
      });
    });
  }, [S.noteBg, S.noteRing, S.savedHighlightBg, S.savedHighlightRing, activeAnnotationId, annotations, pageNum, rightPageNum]);

  // ── Inject theme-specific ::selection colour into <head> ─────────────────
  // pdf_viewer.min.css (CDN) defines its own blue ::selection rule inside <head>.
  // We must also write into <head> — and after it — to win the cascade.
  useEffect(() => {
    const id = "reader-selection-override";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("style");
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = `.textLayer ::selection { background: ${S.selectBg} !important; }`;
  }, [S.selectBg]);

  useEffect(() => {
    const leftDiv = textLayerLeftRef.current;
    const rightDiv = textLayerRightRef.current;

    const syncActiveSpeechHighlight = () => {
      const spans = [
        ...(leftDiv?.querySelectorAll("span") || []),
        ...(rightDiv?.querySelectorAll("span") || []),
      ];

      spans.forEach((span) => {
        if (span.dataset.speechTinted === "true") {
          span.style.background = "none";
          span.dataset.speechTinted = "false";
        }
        span.style.outline = "none";
        span.style.outlineOffset = "";
      });

      applyAnnotationHighlights();

      if (!isSpeaking || speechBoundary.charIndex === undefined || !extractedText) return;

      const cleanText = extractedText.replace(/\s+/g, " ").trim();
      let currentOffset = 0;
      const targetIndex = speechBoundary.charIndex;

      for (const span of spans) {
        const text = (span.textContent || "").replace(/\s+/g, " ").trim();
        if (!text) continue;

        const start = currentOffset;
        const end = currentOffset + text.length;

        if (targetIndex >= start && targetIndex <= end) {
          if (span.dataset.annotationHighlight !== "true") {
            span.style.background = S.highlightBg;
            span.dataset.speechTinted = "true";
          }
          span.style.outline = `2px solid ${S.highlightRing}`;
          span.style.outlineOffset = "2px";
          span.style.borderRadius = "4px";
          span.scrollIntoView({ block: "nearest", behavior: "smooth" });
          break;
        }

        currentOffset += text.length + 1;
      }
    };

    syncActiveSpeechHighlight();

    const observer = new MutationObserver(syncActiveSpeechHighlight);
    const options = { childList: true, subtree: true };
    if (leftDiv) observer.observe(leftDiv, options);
    if (rightDiv) observer.observe(rightDiv, options);

    return () => observer.disconnect();
  }, [S.highlightBg, S.highlightRing, applyAnnotationHighlights, extractedText, isSpeaking, speechBoundary.charIndex]);

  // ── 1. Inject PDF.js from CDN ───────────────────────────────────────────────
  useEffect(() => {
    // Inject the base PDF.js viewer stylesheet for text layer alignment
    const linkId = "pdfjs-viewer-css";
    if (!document.getElementById(linkId)) {
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.href = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf_viewer.min.css";
      document.head.appendChild(link);
    }

    if (window.pdfjsLib) { setPdfLibLoaded(true); return; }

    const s = document.createElement("script");
    s.src   = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.min.js";
    s.async = true;
    s.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js";
      setPdfLibLoaded(true);
    };
    s.onerror = () => setLoadError("Could not load PDF engine. Check your network.");
    document.head.appendChild(s);
  }, []);

  // ── 2. Load the PDF document ────────────────────────────────────────────────
  useEffect(() => {
    if (format === "epub") return;
    if (!pdfLibLoaded || !book?.pdfPath) return;

    let cancelled = false;
    setLoadingPdf(true);
    setLoadError("");
    setPdf(null);

    const loadTask = window.pdfjsLib.getDocument(book.pdfPath);
    loadTask.promise.then(
      (loaded) => {
        if (cancelled) return;
        setPdf(loaded);
        setNumPages(loaded.numPages);



        const saved = localStorage.getItem(`bookmark_${book.id}`);
        if (saved) {
          const n = parseInt(saved, 10);
          if (n >= 1 && n <= loaded.numPages) setPageNum(n);
        } else {
          setPageNum(1);
        }
        setLoadingPdf(false);
      },
      (err) => {
        if (cancelled) return;
        setLoadError(`Could not open this PDF. ${err.message}`);
        setLoadingPdf(false);
      }
    );

    return () => {
      cancelled = true;
      loadTask.destroy?.();
    };
  }, [pdfLibLoaded, book, format]);

  // Handle layout mode auto-switching when resizing across the 1024px threshold
  useEffect(() => {
    let lastWidth = window.innerWidth;
    function handleResizeLayout() {
      const width = window.innerWidth;
      const wasLaptop = lastWidth > 1024;
      const isLaptop = width > 1024;
      if (wasLaptop !== isLaptop) {
        setSpreadMode(isLaptop);
      }
      lastWidth = width;
    }
    window.addEventListener("resize", handleResizeLayout);
    return () => window.removeEventListener("resize", handleResizeLayout);
  }, []);

  // Handle zoom fitting dynamically and fluidly on resize or layout changes
  useEffect(() => {
    if (format === "epub" || !pdf) return;
    
    let timeoutId = null;
    function updateZoom() {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        pdf.getPage(1).then((page) => {
          const pageWidth = page.getViewport({ scale: 1.0 }).width;
          const padding = window.innerWidth <= 768 ? 20 : 64;
          const availableWidth = spreadMode 
            ? (window.innerWidth - padding - 20) / 2 
            : (window.innerWidth - padding);
          const fitZoom = Number((availableWidth / pageWidth).toFixed(2));
          setZoom(Math.max(0.4, Math.min(fitZoom, 1.8)));
        }).catch((err) => {
          console.warn("Resize: Could not determine page viewport width:", err);
        });
      }, 100);
    }

    window.addEventListener("resize", updateZoom);
    updateZoom();

    return () => {
      window.removeEventListener("resize", updateZoom);
      clearTimeout(timeoutId);
    };
  }, [pdf, spreadMode, format]);

  // ── 3. Render pages onto canvases ───────────────────────────────────────────
  const renderPages = useCallback(() => {
    if (format === "epub") return;
    if (!pdf) return;

    renderLeftRef.current?.cancel();
    renderRightRef.current = null;
    renderLeftRef.current  = null;

    // --- Left / single page ---
    const leftTask = startRender(pdf, pageNum, zoom, canvasLeftRef.current, textLayerLeftRef.current);
    if (leftTask) {
      renderLeftRef.current = leftTask;
      leftTask.promise.catch((e) => {
        if (e?.name !== "RenderingCancelledException") {
          console.warn("[Reader] left render:", e?.message ?? e);
        }
      });
    }

    // --- Right page (spread mode) ---
    const rightNum = pageNum + 1;
    if (spreadMode && rightNum <= pdf.numPages && canvasRightRef.current) {
      const rightTask = startRender(pdf, rightNum, zoom, canvasRightRef.current, textLayerRightRef.current);
      if (rightTask) {
        renderRightRef.current = rightTask;
        rightTask.promise.catch((e) => {
          if (e?.name !== "RenderingCancelledException") {
            console.warn("[Reader] right render:", e?.message ?? e);
          }
        });
      }
    } else {
      if (textLayerRightRef.current) {
        textLayerRightRef.current.innerHTML = "";
      }
      if (canvasRightRef.current && typeof canvasRightRef.current.getContext === "function") {
        const ctx = canvasRightRef.current.getContext("2d");
        ctx.clearRect(0, 0, canvasRightRef.current.width, canvasRightRef.current.height);
      }
    }

    if (book) {
      localStorage.setItem(`bookmark_${book.id}`, String(pageNum));
      localStorage.setItem("ebenezer_progress_last_updated", String(Date.now()));
    }
  }, [pdf, pageNum, zoom, spreadMode, book, format]);

  useEffect(() => {
    renderPages();
  }, [renderPages]);

  // ── 2b. Inject Epub.js from CDN ───────────────────────────────────────────────
  useEffect(() => {
    if (format !== "epub") return;
    if (window.ePub) { setEpubLibLoaded(true); return; }

    const loadJSZip = () => {
      if (window.JSZip) {
        loadEpubLib();
        return;
      }
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
      s.async = true;
      s.onload = loadEpubLib;
      s.onerror = () => {
        // Fallback JSZip if cdnjs fails
        const fallbackZip = document.createElement("script");
        fallbackZip.src = "https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js";
        fallbackZip.async = true;
        fallbackZip.onload = loadEpubLib;
        fallbackZip.onerror = () => setLoadError("Could not load zip engine (JSZip).");
        document.head.appendChild(fallbackZip);
      };
      document.head.appendChild(s);
    };

    const loadEpubLib = () => {
      if (window.ePub) {
        setEpubLibLoaded(true);
        return;
      }
      const s = document.createElement("script");
      s.src = "https://cdn.jsdelivr.net/npm/epubjs/dist/epub.min.js";
      s.async = true;
      s.onload = () => {
        setEpubLibLoaded(true);
      };
      s.onerror = () => {
        // Fallback to cdnjs version 0.3.93 if jsDelivr fails
        const fallbackEpub = document.createElement("script");
        fallbackEpub.src = "https://cdnjs.cloudflare.com/ajax/libs/epub.js/0.3.93/epub.min.js";
        fallbackEpub.async = true;
        fallbackEpub.onload = () => {
          setEpubLibLoaded(true);
        };
        fallbackEpub.onerror = () => setLoadError("Could not load EPUB engine. Check your network.");
        document.head.appendChild(fallbackEpub);
      };
      document.head.appendChild(s);
    };

    loadJSZip();
  }, [format]);

  // ── 2c. Load the EPUB document ────────────────────────────────────────────────
  useEffect(() => {
    if (format !== "epub" || !epubLibLoaded || !book?.epubPath || !epubViewerRef.current) return;

    let cancelled = false;
    setLoadingPdf(true);
    setLoadError("");

    const epubBook = window.ePub(book.epubPath);
    epubBookRef.current = epubBook;

    epubBook.ready.then(() => {
      if (cancelled) return;

      const rend = epubBook.renderTo(epubViewerRef.current, {
        width: "100%",
        height: "100%",
        allowScriptedContent: true
      });

      setRendition(rend);

      const savedLocation = localStorage.getItem(`bookmark_epub_${book.id}`);
      rend.display(savedLocation || undefined);

      rend.on("relocated", (location) => {
        if (cancelled) return;
        localStorage.setItem(`bookmark_epub_${book.id}`, location.start.cfi);
        localStorage.setItem("ebenezer_progress_last_updated", String(Date.now()));
        
        // Extract text from the iframe body to support Read Aloud (TTS)
        try {
          const iframe = epubViewerRef.current.querySelector("iframe");
          if (iframe && iframe.contentDocument) {
            const bodyText = iframe.contentDocument.body.innerText || "";
            setEpubText(bodyText);
          }
        } catch (e) {
          console.warn("[EPUB text extract] Failed to get iframe text:", e);
        }

        if (epubBook.locations && epubBook.locations.length > 0) {
          const progressPercent = epubBook.locations.percentageFromCfi(location.start.cfi);
          const estimatedPage = Math.round(progressPercent * book.pages) || 1;
          setPageNum(estimatedPage);
        }
      });

      epubBook.locations.generate(1024).then(() => {
        if (cancelled) return;
        const currentCfi = rend.location?.start?.cfi;
        if (currentCfi) {
          const progressPercent = epubBook.locations.percentageFromCfi(currentCfi);
          const estimatedPage = Math.round(progressPercent * book.pages) || 1;
          setPageNum(estimatedPage);
        }
      });

      setNumPages(book.pages || 100);
      setLoadingPdf(false);
    }).catch((err) => {
      if (cancelled) return;
      setLoadError(`Could not open this EPUB: ${err.message}`);
      setLoadingPdf(false);
    });

    return () => {
      cancelled = true;
      try {
        rendition?.destroy();
      } catch (e) {}
    };
  }, [epubLibLoaded, book, format]);

  // ── 2d. Apply theme dynamically to EPUB rendition ─────────────────────────────
  useEffect(() => {
    if (format !== "epub" || !rendition) return;

    const themeBg = S.bg;
    const themeColor = S.text;
    const themeFont = "'Cormorant Garamond', serif";

    rendition.themes.register("custom", {
      body: {
        background: `${themeBg} !important`,
        color: `${themeColor} !important`,
        "font-family": `${themeFont} !important`,
        "font-size": "18px !important",
        "line-height": "1.6 !important",
        padding: "0 20px !important"
      },
      p: {
        "font-family": `${themeFont} !important`,
        "font-size": "18px !important",
      },
      h1: {
        color: `${S.accent} !important`
      },
      h2: {
        color: `${S.accent} !important`
      },
      h3: {
        color: `${S.accent} !important`
      }
    });

    rendition.themes.select("custom");
  }, [rendition, theme, format, S]);

  useEffect(() => {
    const timer = setTimeout(applyAnnotationHighlights, 120);
    return () => clearTimeout(timer);
  }, [applyAnnotationHighlights, pdf, zoom, spreadMode]);


  // Cleanup on unmount
  useEffect(() => {
    return () => {
      renderLeftRef.current?.cancel();
      renderRightRef.current?.cancel();
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // ── 4. Navigation ──────────────────────────────────────────────────────────
  const step = spreadMode ? 2 : 1;

  const goNext = useCallback(() => {
    stopSpeechOnNavigation();
    if (format === "epub" && rendition) {
      rendition.next();
    } else {
      setPageNum((p) => Math.min(p + step, numPages));
    }
  }, [step, numPages, stopSpeechOnNavigation, format, rendition]);

  const goPrev = useCallback(() => {
    stopSpeechOnNavigation();
    if (format === "epub" && rendition) {
      rendition.prev();
    } else {
      setPageNum((p) => Math.max(p - step, 1));
    }
  }, [step, stopSpeechOnNavigation, format, rendition]);

  // Sync page-number input field
  useEffect(() => {
    if (pageInputRef.current) pageInputRef.current.value = pageNum;
  }, [pageNum]);

  const handlePageSubmit = (e) => {
    e.preventDefault();
    stopSpeechOnNavigation();
    const v = parseInt(pageInputRef.current?.value, 10);
    if (v >= 1 && v <= numPages) setPageNum(v);
    else if (pageInputRef.current) pageInputRef.current.value = pageNum;
  };

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === "ArrowRight" || e.key === "l") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft" || e.key === "h") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "Escape") {
        navigate(`/book/${bookId}`, { replace: true });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, navigate, bookId]);

  // ── Click-to-seek: tap a word while TTS is playing to jump there ───────────
  const handleTextLayerClick = useCallback((e) => {
    if (!isSpeaking) return; // only active when TTS is playing

    const clickedSpan = e.target.closest("span");
    if (!clickedSpan) return;

    // Collect all spans in reading order (left page first, then right)
    const leftSpans  = Array.from(textLayerLeftRef.current?.querySelectorAll("span")  || []);
    const rightSpans = Array.from(textLayerRightRef.current?.querySelectorAll("span") || []);
    const allSpans   = [...leftSpans, ...rightSpans];

    // Walk spans, counting chars until we hit the clicked one
    const cleanExtracted = (extractedText || "").replace(/\s+/g, " ").trim();
    let charOffset = 0;
    for (const span of allSpans) {
      const spanText = (span.textContent || "").replace(/\s+/g, " ").trim();
      if (!spanText) continue;
      if (span === clickedSpan) break;
      charOffset += spanText.length + 1; // +1 for the space separator
    }
    charOffset = Math.max(0, Math.min(charOffset, cleanExtracted.length - 1));

    // Restart TTS from the clicked word
    window.speechSynthesis.cancel();
    startSpeech(charOffset);
  }, [isSpeaking, extractedText, startSpeech]);

  const clearSelectedText = useCallback(() => {
    setSelectedTextInfo(null);
    if (typeof window !== "undefined") {
      window.getSelection()?.removeAllRanges();
    }
  }, []);

  const captureTextSelection = useCallback(() => {
    if (typeof window === "undefined") return;
    const selection = window.getSelection();
    const text = selection?.toString().replace(/\s+/g, " ").trim();

    if (!selection || selection.rangeCount === 0 || !text || text.length < 2) {
      setSelectedTextInfo(null);
      return;
    }

    const range = selection.getRangeAt(0);
    const layerElement = (
      selection.anchorNode?.parentElement?.closest?.(".textLayer[data-page]") ||
      selection.focusNode?.parentElement?.closest?.(".textLayer[data-page]") ||
      range.commonAncestorContainer?.parentElement?.closest?.(".textLayer[data-page]")
    );

    if (!layerElement) {
      setSelectedTextInfo(null);
      return;
    }

    const rect = range.getBoundingClientRect();
    if (!rect.width && !rect.height) return;

    const spans = Array.from(layerElement.querySelectorAll("span"));
    const spanIndex = spans.findIndex((span) => selection.containsNode(span, true));

    setSelectedTextInfo({
      text,
      page: parseInt(layerElement.dataset.page, 10) || pageNum,
      spanIndex: spanIndex >= 0 ? spanIndex : null,
      rect: {
        left: Math.min(Math.max(rect.left + rect.width / 2, 172), window.innerWidth - 172),
        top: Math.max(rect.top - 54, 62),
      },
    });
  }, [pageNum]);

  // Global listener for text selection changes to ensure mobile (Android/iOS) selection works natively
  useEffect(() => {
    if (typeof window === "undefined") return;

    let timeoutId = null;
    const handleSelectionChange = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        captureTextSelection();
      }, 180);
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
      clearTimeout(timeoutId);
    };
  }, [captureTextSelection]);

  const saveAnnotation = useCallback((type, source, note = "") => {
    if (!source?.text) return;
    const annotation = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      type,
      text: source.text,
      note: note.trim(),
      page: source.page || pageNum,
      spanIndex: Number.isInteger(source.spanIndex) ? source.spanIndex : null,
      createdAt: new Date().toISOString(),
    };

    persistAnnotations((prev) => [annotation, ...prev]);
    setShowNotesPanel(true);
    setPendingNote(null);
    setNoteDraft("");
    clearSelectedText();
  }, [clearSelectedText, pageNum, persistAnnotations]);

  const openNoteComposer = useCallback((source) => {
    setPendingNote(source);
    setNoteDraft("");
    setShowNotesPanel(true);
    setSelectedTextInfo(null);
  }, []);

  const handleAskScholar = useCallback(async (passageInfo) => {
    if (!passageInfo?.text) return;
    setScholarPassage(passageInfo);
    setScholarHistory([]);
    setScholarError("");
    setScholarLoading(true);
    setShowScholarDrawer(true);
    setShowNotesPanel(false); // Close notes panel to avoid overlap
    setSelectedTextInfo(null); // Dismiss the popup toolbar

    try {
      const token = user ? await getAuthToken() : "";
      const res = await fetch("/api/explain", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          text: passageInfo.text,
          bookTitle: book?.title || "Unknown Book",
          bookAuthor: book?.author || "Unknown Author",
          page: passageInfo.page || pageNum,
        }),
      });

      if (!res.ok) {
        throw new Error(`Failed to request explanation: ${res.statusText}`);
      }

      const data = await res.json();
      if (data.error) {
        throw new Error(data.error);
      }

      setScholarHistory([
        { role: "assistant", content: data.explanation }
      ]);
    } catch (err) {
      console.error("[ReaderPage] Ask Scholar failed:", err);
      setScholarError(err.message || "Failed to load scholar explanation.");
    } finally {
      setScholarLoading(false);
    }
  }, [book, pageNum, user]);

  const handleSendScholarMessage = useCallback(async (e) => {
    if (e) e.preventDefault();
    const query = scholarInput.trim();
    if (!query || scholarLoading || !scholarPassage) return;

    const newHistory = [
      ...scholarHistory,
      { role: "user", content: query }
    ];

    setScholarHistory(newHistory);
    setScholarInput("");
    setScholarLoading(true);
    setScholarError("");

    try {
      const token = user ? await getAuthToken() : "";
      const res = await fetch("/api/explain", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          text: scholarPassage.text,
          bookTitle: book?.title || "Unknown Book",
          bookAuthor: book?.author || "Unknown Author",
          page: scholarPassage.page || pageNum,
          history: newHistory,
        }),
      });

      if (!res.ok) {
        throw new Error(`Failed to request response: ${res.statusText}`);
      }

      const data = await res.json();
      if (data.error) {
        throw new Error(data.error);
      }

      setScholarHistory([
        ...newHistory,
        { role: "assistant", content: data.explanation }
      ]);
    } catch (err) {
      console.error("[ReaderPage] Send Scholar Message failed:", err);
      setScholarError(err.message || "Failed to load scholar response.");
    } finally {
      setScholarLoading(false);
    }
  }, [book, pageNum, scholarInput, scholarHistory, scholarLoading, scholarPassage, user]);

  const deleteAnnotation = useCallback((id) => {
    persistAnnotations((prev) => prev.filter((item) => item.id !== id));
  }, [persistAnnotations]);

  const jumpToAnnotation = useCallback((item) => {
    stopSpeechOnNavigation();
    setActiveAnnotationId(item.id);
    setPageNum(Math.min(Math.max(item.page, 1), numPages));
    setShowNotesPanel(false);
  }, [numPages, stopSpeechOnNavigation]);

  useEffect(() => {
    if (!activeAnnotationId) return;

    const timer = setTimeout(() => {
      applyAnnotationHighlights();
      const target = scrollViewportRef.current?.querySelector(
        `.textLayer span[data-annotation-id="${activeAnnotationId}"]`
      );
      target?.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
    }, 180);

    const clearTimer = setTimeout(() => {
      setActiveAnnotationId(null);
    }, 2600);

    return () => {
      clearTimeout(timer);
      clearTimeout(clearTimer);
    };
  }, [activeAnnotationId, applyAnnotationHighlights, pageNum]);

  // Reset scroll to top-left on every page turn
  useEffect(() => {
    if (scrollViewportRef.current) {
      scrollViewportRef.current.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [pageNum]);



  const changeTheme = (t) => {
    if (setTheme) setTheme(t);
  };

  // ── Loading / not-found guard ──────────────────────────────────────────────
  if (booksLoading) {
    return (
      <div style={{ display:"flex", flexDirection:"column", height:"100vh", alignItems:"center", justifyContent:"center", background:S.bg, color:S.text, fontFamily:"'Outfit', sans-serif", gap:"20px" }}>
        <div style={{ position:"relative", width:"72px", height:"72px" }}>
          <div style={{ position:"absolute", inset:0, borderRadius:"50%", border:`3px solid ${S.panelBorder}`, borderTopColor:S.accent, animation:"spin 0.9s linear infinite" }} />
          <div style={{ position:"absolute", inset:"12px", borderRadius:"50%", border:`2px solid ${S.panelBorder}40`, borderBottomColor:S.accent, animation:"spin 1.4s linear infinite reverse" }} />
          <div style={{ position:"absolute", inset:"24px", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1rem" }}>📖</div>
        </div>
        <div style={{ textAlign:"center" }}>
          <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:"1.25rem", fontWeight:600, marginBottom:"4px" }}>Loading your library…</div>
          <div style={{ fontSize:"0.75rem", opacity:0.45 }}>Preparing the reading room</div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!book) {
    return (
      <div style={{ display:"flex", flexDirection:"column", height:"100vh", alignItems:"center", justifyContent:"center", background:S.bg, color:S.text, fontFamily:"'Outfit', sans-serif", gap:"16px", padding:"2rem", textAlign:"center" }}>
        <div style={{ width:"80px", height:"80px", borderRadius:"50%", background:`${S.accent}18`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"2.2rem", marginBottom:"4px" }}>📚</div>
        <h2 style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:"2rem", margin:0 }}>Book Not Found</h2>
        <p style={{ fontSize:"0.88rem", opacity:0.7, maxWidth:"360px", lineHeight:1.65, margin:0 }}>
          The volume you're looking for could not be found in the catalog.
        </p>
        <button onClick={() => navigate("/")} style={{ padding:"10px 24px", background:S.accent, color:"#fff", border:"none", borderRadius:"30px", fontSize:"0.88rem", fontWeight:700, cursor:"pointer", boxShadow:`0 4px 16px ${S.accent}44`, fontFamily:"'Outfit', sans-serif", marginTop:"8px" }}>
          Return to Library
        </button>
      </div>
    );
  }

  const showRight    = spreadMode && rightPageNum <= numPages;
  const progress     = numPages ? Math.round((pageNum / numPages) * 100) : 0;

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: "100vh",
      background: S.bg, color: S.text,
      fontFamily: "'Outfit', sans-serif",
      transition: "background 0.3s, color 0.3s",
      overflow: "hidden",
    }}>

      {/* ═══════════════════════════════════════ TOOLBAR ═══════════════════ */}
      <ReaderToolbar
        S={S}
        theme={theme}
        book={book}
        loadingPdf={loadingPdf}
        loadError={loadError}
        pageNum={pageNum}
        numPages={numPages}
        showRight={showRight}
        rightPageNum={rightPageNum}
        spreadMode={spreadMode}
        setSpreadMode={setSpreadMode}
        zoom={zoom}
        setZoom={setZoom}
        changeTheme={changeTheme}
        showAudioPlayer={showAudioPlayer}
        setShowAudioPlayer={setShowAudioPlayer}
        isSpeaking={isSpeaking}
        stopSpeech={stopSpeech}
        startSpeech={startSpeech}
        toggleSpeech={toggleSpeech}
        playDisabled={playDisabled}
        voiceGroups={voiceGroups}
        selectedVoiceName={selectedVoiceName}
        setSelectedVoiceName={setSelectedVoiceName}
        setVoiceGender={setVoiceGender}
        showNotesPanel={showNotesPanel}
        setShowNotesPanel={setShowNotesPanel}
        annotationCount={annotations.length}
        goPrev={goPrev}
        goNext={goNext}
        handlePageSubmit={handlePageSubmit}
        pageInputRef={pageInputRef}
        navigate={navigate}
        stopSpeechOnNavigation={stopSpeechOnNavigation}
        selectedTextInfo={selectedTextInfo}
        saveAnnotation={saveAnnotation}
        openNoteComposer={openNoteComposer}
        clearSelectedText={clearSelectedText}
        handleAskScholar={handleAskScholar}
      />

      {/* ═══════════════════════════════════════ PROGRESS BAR ═════════════ */}
      <div style={{ height: "3px", background: S.panelBorder, flexShrink: 0, position: "relative" }}>
        <div style={{
          height: "100%", width: `${progress}%`,
          background: `linear-gradient(90deg, ${S.accent} 0%, ${S.accent}cc 60%, #e5b84a 100%)`,
          transition: "width 0.5s cubic-bezier(0.16,1,0.3,1)",
          boxShadow: progress > 0 ? `0 0 8px ${S.accent}66` : "none",
        }} />
        {progress > 0 && progress < 100 && (
          <div style={{ position:"absolute", right:`calc(${100 - progress}% - 4px)`, top:"-3px", width:"8px", height:"8px", borderRadius:"50%", background:S.accent, boxShadow:`0 0 6px ${S.accent}`, transition:"right 0.5s cubic-bezier(0.16,1,0.3,1)" }} />
        )}
      </div>

      {/* ═══════════════════════════════════════ CANVAS VIEWPORT ══════════ */}
      <div
        ref={scrollViewportRef}
        style={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          padding: "2rem 1rem",
          background: S.pageBg,
          transition: "background 0.3s, margin-right 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          marginRight: (showNotesPanel || showScholarDrawer) && window.innerWidth > 1024 ? "385px" : "0px",
        }}
      >

        {/* Loading state */}
        {(!pdfLibLoaded || loadingPdf) && !loadError && (
          <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:"1.25rem", color:S.text }}>
            <div style={{ position:"relative", width:"60px", height:"60px" }}>
              <div style={{ position:"absolute", inset:0, borderRadius:"50%", border:`3px solid ${S.panelBorder}`, borderTopColor:S.accent, animation:"spin 0.9s linear infinite" }} />
              <div style={{ position:"absolute", inset:"10px", borderRadius:"50%", border:`2px solid ${S.panelBorder}40`, borderBottomColor:S.accent, animation:"spin 1.4s linear infinite reverse" }} />
              <div style={{ position:"absolute", inset:"20px", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.9rem" }}>📖</div>
            </div>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:"0.95rem", fontWeight:600, marginBottom:"4px" }}>
                {!pdfLibLoaded ? "Initialising reader…" : `Opening "${book.title}"…`}
              </div>
              {pageNum > 1 && (
                <div style={{ fontSize:"0.75rem", opacity:0.45 }}>Resuming from page {pageNum}</div>
              )}
            </div>
          </div>
        )}

        {/* Error state */}
        {loadError && (
          <div style={{ maxWidth:"480px", margin:"5rem auto 0", padding:"2.5rem", borderRadius:"16px", background:`rgba(220,50,50,0.07)`, border:"1px solid rgba(220,50,50,0.2)", textAlign:"center" }}>
            <div style={{ width:"60px", height:"60px", borderRadius:"50%", background:"rgba(220,50,50,0.1)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 1rem", fontSize:"1.75rem" }}>⚠️</div>
            <h3 style={{ margin:"0 0 0.5rem", color:"#E55", fontSize:"1.15rem", fontFamily:"'Cormorant Garamond', serif" }}>Could Not Open Document</h3>
            <p style={{ fontSize:"0.85rem", lineHeight:1.7, margin:"0 0 1.5rem", opacity:0.75 }}>{loadError}</p>
            <button onClick={() => navigate(`/book/${book.id}`, { replace:true })} style={{ background:S.accent, color:"#fff", border:"none", borderRadius:"30px", padding:"9px 22px", fontSize:"0.85rem", fontWeight:700, cursor:"pointer", boxShadow:`0 4px 14px ${S.accent}44` }}>
              ← Back to Book
            </button>
          </div>
        )}

        {/* ── EPUB Pages wrapper ── */}
        {!loadError && format === "epub" && (
          <div style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            width: "100%",
            maxWidth: "800px",
            height: "calc(100vh - 150px)",
            animation: "fadeIn 0.3s ease both",
            visibility: loadingPdf ? "hidden" : "visible",
            opacity: loadingPdf ? 0 : 1,
            pointerEvents: loadingPdf ? "none" : "auto",
            position: loadingPdf ? "absolute" : "relative",
          }}>
            {/* Ghost prev-page zone */}
            <div
              onClick={goPrev}
              title="Previous page (←)"
              style={{
                width: "48px", alignSelf: "stretch", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
                color: S.text, fontSize: "1.5rem",
                opacity: 0.1, transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.7"; }}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.1")}
            >‹</div>

            {/* EPUB container */}
            <div 
              ref={epubViewerRef}
              style={{
                flex: 1,
                background: S.bg,
                borderRadius: "8px",
                boxShadow: `0 12px 48px ${S.shadow}`,
                padding: "20px",
                overflow: "hidden",
                border: `1px solid ${S.panelBorder}`,
                transition: "background 0.3s, border-color 0.3s"
              }}
            />

            {/* Ghost next-page zone */}
            <div
              onClick={goNext}
              title="Next page (→)"
              style={{
                width: "48px", alignSelf: "stretch", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
                color: S.text, fontSize: "1.5rem",
                opacity: 0.1, transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.7"; }}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.1")}
            >›</div>
          </div>
        )}

        {/* ── PDF Pages wrapper ── */}
        {!loadingPdf && !loadError && format !== "epub" && (
          <div style={{
            display: "flex",
            alignItems: "flex-start",
            animation: "fadeIn 0.3s ease both",
          }}>
            {/* Ghost prev-page zone */}
            <div
              onClick={goPrev}
              title="Previous page (←)"
              style={{
                width: "48px", alignSelf: "stretch", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: pageNum <= 1 ? "default" : "w-resize",
                color: S.text, fontSize: "1.5rem",
                opacity: 0, transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => { if (pageNum > 1) e.currentTarget.style.opacity = "0.4"; }}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0")}
            >‹</div>

            {/* Book pages container */}
            <div style={{
              display: "flex",
              alignItems: "stretch",
              borderRadius: spreadMode ? "3px 8px 8px 3px" : "6px",
              boxShadow: `0 12px 48px ${S.shadow}`,
              overflow: "hidden",
            }}>
              {/* ── LEFT / SINGLE canvas ── */}
              <div style={{ position: "relative", background: "#fff" }}>
                <canvas
                  ref={canvasLeftRef}
                  id="reader-canvas-left"
                  style={{
                    display: "block",
                    filter: S.canvasFilter,
                    transition: "filter 0.3s",
                  }}
                />
                {/* Left Text Layer Overlay */}
                <div
                  ref={textLayerLeftRef}
                  className="textLayer"
                  data-page={pageNum}
                  data-speaking={isSpeaking ? "true" : "false"}
                  onClick={handleTextLayerClick}
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    opacity: 1,
                    zIndex: 2,
                  }}
                />
                {/* Gutter shadow toward spine */}
                {spreadMode && (
                  <div style={{
                    position: "absolute", top: 0, right: 0,
                    width: "32px", height: "100%",
                    background: "linear-gradient(to right, transparent, rgba(0,0,0,0.07))",
                    pointerEvents: "none",
                  }} />
                )}
              </div>

              {/* ── SPINE (visible only in spread mode) ── */}
              <div style={{
                width: spreadMode ? "16px" : "0px",
                flexShrink: 0,
                overflow: "hidden",
                background: S.spineBg,
                position: "relative",
                transition: "width 0.25s ease",
              }}>
                <div style={{
                  position: "absolute", top: 0, left: "3px",
                  width: "4px", height: "100%",
                  background: S.spineShine,
                  borderRadius: "2px",
                }} />
                <div style={{
                  position: "absolute", top: 0, right: 0,
                  width: "5px", height: "100%",
                  background: "rgba(0,0,0,0.15)",
                }} />
              </div>

              {/* ── RIGHT canvas (always mounted, hidden when not in spread) ── */}
              <div style={{
                position: "relative",
                background: "#fff",
                display: spreadMode ? "block" : "none",
              }}>
                <div style={{
                  position: "absolute", top: 0, left: 0,
                  width: "32px", height: "100%",
                  background: "linear-gradient(to left, transparent, rgba(0,0,0,0.05))",
                  pointerEvents: "none", zIndex: 1,
                }} />

                {showRight ? (
                  <>
                    <canvas
                      ref={canvasRightRef}
                      id="reader-canvas-right"
                      style={{
                        display: "block",
                        filter: S.canvasFilter,
                        transition: "filter 0.3s",
                      }}
                    />
                    {/* Right Text Layer Overlay */}
                    <div
                      ref={textLayerRightRef}
                      className="textLayer"
                      data-page={rightPageNum}
                      data-speaking={isSpeaking ? "true" : "false"}
                      onClick={handleTextLayerClick}
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        width: "100%",
                        height: "100%",
                        overflow: "hidden",
                        opacity: 1,
                        zIndex: 2,
                      }}
                    />
                  </>
                ) : (
                  /* Blank verso page at end of book */
                  <div
                    style={{
                      width:  canvasLeftRef.current?.style.width  || "400px",
                      height: canvasLeftRef.current?.style.height || "566px",
                      background: "#fff",
                      filter: S.canvasFilter,
                      transition: "filter 0.3s",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <div style={{
                      width: "1px", height: "60%",
                      background: S.panelBorder,
                      transition: "background 0.3s",
                    }} />
                  </div>
                )}
              </div>
            </div>

            {/* Ghost next-page zone */}
            <div
              onClick={goNext}
              title="Next page (→)"
              style={{
                width: "48px", alignSelf: "stretch", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: pageNum >= numPages ? "default" : "e-resize",
                color: S.text, fontSize: "1.5rem",
                opacity: 0, transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => { if (pageNum < numPages) e.currentTarget.style.opacity = "0.4"; }}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0")}
            >›</div>
          </div>
        )}

        {/* Floating page pill */}
        {!loadingPdf && !loadError && (
          <div style={{
            position: "fixed",
            bottom: "1.5rem",
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(20,14,6,0.55)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            color: "#fff",
            borderRadius: "20px",
            padding: "5px 16px",
            fontSize: "0.72rem",
            fontWeight: 600,
            letterSpacing: "0.06em",
            pointerEvents: "none",
            whiteSpace: "nowrap",
            zIndex: 30,
          }}>
            {showRight
              ? `pp. ${pageNum}–${rightPageNum} of ${numPages}`
              : `p. ${pageNum} of ${numPages}`
            }{" · "}{progress}%
          </div>
        )}
      </div>

      {selectedTextInfo && window.innerWidth > 768 && (
        <div style={{
          position: "fixed",
          left: `${selectedTextInfo.rect.left}px`,
          top: `${selectedTextInfo.rect.top}px`,
          transform: "translateX(-50%)",
          zIndex: 120,
          background: S.panelBg,
          border: `1px solid ${S.panelBorder}`,
          borderRadius: "999px",
          padding: "6px",
          display: "flex",
          gap: "6px",
          boxShadow: `0 12px 30px ${S.shadow}`,
          transition: "background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease",
        }}>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleAskScholar(selectedTextInfo)}
            style={{
              ...selectionBtnStyle(),
              background: "rgba(200, 146, 42, 0.15)",
              color: "var(--gold)",
              boxShadow: "inset 0 0 0 1px rgba(200, 146, 42, 0.4)",
              transition: "all 0.3s ease",
              marginRight: "2px",
            }}
          >
            🎓 Explain
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => saveAnnotation("highlight", selectedTextInfo)}
            style={{
              ...selectionBtnStyle(),
              background: S.savedHighlightBg,
              color: S.text,
              boxShadow: `inset 0 0 0 1px ${S.savedHighlightRing}`,
              transition: "all 0.3s ease",
            }}
          >
            Highlight
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => openNoteComposer(selectedTextInfo)}
            style={{
              ...selectionBtnStyle(true),
              background: S.noteBg,
              color: theme === "dark" ? S.noteText : "#1F4E6D",
              boxShadow: `inset 0 0 0 1px ${S.noteRing}`,
              transition: "all 0.3s ease",
            }}
          >
            Add Note
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={clearSelectedText}
            title="Dismiss"
            style={{
              ...selectionBtnStyle(),
              minWidth: "30px",
              padding: "0 8px",
              color: S.text,
              opacity: 0.6,
              background: "transparent",
              transition: "all 0.3s ease",
            }}
          >
            ✕
          </button>
        </div>
      )}


      {showScholarDrawer && scholarPassage && (
        <aside style={{
          position: "fixed",
          top: "55px",
          right: 0,
          bottom: 0,
          width: "min(385px, 94vw)",
          background: S.panelBg,
          borderLeft: `1px solid ${S.panelBorder}`,
          boxShadow: `-12px 0 34px ${S.shadow}`,
          zIndex: 110,
          display: "flex",
          flexDirection: "column",
          color: S.text,
          transition: "all 0.3s ease",
          animation: "slideLeft 0.3s ease both",
        }}>
          {/* Header */}
          <div style={{
            padding: "1rem",
            borderBottom: `1px solid ${S.panelBorder}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "1rem",
          }}>
            <div>
              <div style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "1.25rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}>
                🎓 <span style={{ color: "var(--gold)" }}>Theological Scholar</span>
              </div>
              <div style={{ fontSize: "0.68rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: "2px" }}>
                AI Reading Room Companion
              </div>
            </div>
            <div style={{ display: "flex", gap: "6px" }}>
              {scholarHistory.length > 0 && (
                <button
                  onClick={() => {
                    setScholarHistory([]);
                    handleAskScholar(scholarPassage);
                  }}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: S.text,
                    opacity: 0.6,
                    fontSize: "0.8rem",
                    cursor: "pointer",
                  }}
                  title="Reset Discussion"
                >
                  🔄 Reset
                </button>
              )}
              <button
                onClick={() => {
                  setShowScholarDrawer(false);
                  setScholarPassage(null);
                }}
                style={{
                  border: "none",
                  background: "transparent",
                  color: S.text,
                  opacity: 0.8,
                  fontSize: "1.1rem",
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {/* Drawer Body (Scrollable) */}
          <div style={{
            flex: 1,
            overflowY: "auto",
            padding: "1rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}>
            {/* Context Excerpt */}
            <div style={{
              background: "rgba(128, 128, 128, 0.04)",
              borderLeft: "3.5px solid var(--gold)",
              borderRadius: "6px",
              padding: "0.85rem 1rem",
            }}>
              <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "6px" }}>
                Highlighted Excerpt (Page {scholarPassage.page})
              </div>
              <div style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "0.95rem",
                fontStyle: "italic",
                lineHeight: 1.45,
                color: S.text,
              }}>
                "{scholarPassage.text}"
              </div>
            </div>

            {/* Chat Feed */}
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {scholarHistory.map((msg, idx) => {
                const isUser = msg.role === "user";
                return (
                  <div
                    key={idx}
                    style={{
                      alignSelf: isUser ? "flex-end" : "flex-start",
                      maxWidth: "90%",
                      background: isUser
                        ? "rgba(128, 128, 128, 0.08)"
                        : "rgba(200, 146, 42, 0.05)",
                      border: `1px solid ${isUser ? S.panelBorder : "rgba(200, 146, 42, 0.15)"}`,
                      borderRadius: isUser ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
                      padding: "0.8rem 1rem",
                      animation: "fadeUp 0.3s ease both",
                    }}
                  >
                    <div style={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: isUser ? "var(--muted)" : "var(--gold)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: "4px",
                    }}>
                      {isUser ? "You" : "Scholar"}
                    </div>
                    <div>
                      {isUser ? (
                        <p style={{ fontSize: "0.86rem", lineHeight: 1.5, margin: 0 }}>
                          {msg.content}
                        </p>
                      ) : (
                        renderMarkdown(msg.content)
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Loader */}
              {scholarLoading && (
                <div style={{
                  alignSelf: "flex-start",
                  background: "rgba(200, 146, 42, 0.04)",
                  border: "1px solid rgba(200, 146, 42, 0.1)",
                  borderRadius: "12px 12px 12px 2px",
                  padding: "0.8rem 1.2rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  animation: "pulseGold 2s infinite ease-in-out",
                }}>
                  <div style={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "var(--gold)",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}>
                    Scholar is thinking...
                  </div>
                  <div style={{ display: "flex", gap: "4px", padding: "4px 0" }}>
                    <div className="dot" style={{ width: "6px", height: "6px", background: "var(--gold)", borderRadius: "50%", animation: "bounceMini 0.6s infinite alternate" }} />
                    <div className="dot" style={{ width: "6px", height: "6px", background: "var(--gold)", borderRadius: "50%", animation: "bounceMini 0.6s infinite alternate 0.2s" }} />
                    <div className="dot" style={{ width: "6px", height: "6px", background: "var(--gold)", borderRadius: "50%", animation: "bounceMini 0.6s infinite alternate 0.4s" }} />
                  </div>
                </div>
              )}

              {scholarError && (
                <div style={{
                  background: "rgba(166, 50, 50, 0.1)",
                  border: "1px solid rgba(166, 50, 50, 0.3)",
                  borderRadius: "8px",
                  padding: "0.75rem 1rem",
                  fontSize: "0.82rem",
                  color: "var(--rust)",
                  lineHeight: 1.4,
                }}>
                  ⚠️ {scholarError}
                </div>
              )}
            </div>
          </div>

          {/* Quick Presets Row (Show only when not loading) */}
          {scholarHistory.length === 1 && !scholarLoading && (
            <div style={{
              padding: "0.5rem 1rem",
              borderTop: `1px dashed ${S.panelBorder}`,
              display: "flex",
              gap: "0.5rem",
              flexWrap: "wrap",
            }}>
              {[
                "Tell me the historical context",
                "Explain the key theology",
                "Simplify this text",
              ].map((preset) => (
                <button
                  key={preset}
                  onClick={() => {
                    setScholarInput(preset);
                  }}
                  style={{
                    background: "rgba(128, 128, 128, 0.05)",
                    border: `1px solid ${S.panelBorder}`,
                    color: S.text,
                    borderRadius: "14px",
                    padding: "4px 10px",
                    fontSize: "0.74rem",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "var(--gold)";
                    e.currentTarget.style.color = "var(--gold)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = S.panelBorder;
                    e.currentTarget.style.color = S.text;
                  }}
                >
                  {preset}
                </button>
              ))}
            </div>
          )}

          {/* Chat Input Footer */}
          <form
            onSubmit={handleSendScholarMessage}
            style={{
              padding: "1rem",
              borderTop: `1px solid ${S.panelBorder}`,
              display: "flex",
              gap: "0.5rem",
              alignItems: "center",
            }}
          >
            <input
              type="text"
              value={scholarInput}
              onChange={(e) => setScholarInput(e.target.value)}
              placeholder="Ask the scholar a question..."
              disabled={scholarLoading}
              style={{
                flex: 1,
                border: `1px solid ${S.panelBorder}`,
                background: "var(--cream)",
                color: S.text,
                borderRadius: "30px",
                padding: "8px 16px",
                fontSize: "0.85rem",
                outline: "none",
                fontFamily: "'Outfit', sans-serif",
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => e.target.style.borderColor = "var(--gold)"}
              onBlur={(e) => e.target.style.borderColor = S.panelBorder}
            />
            <button
              type="submit"
              disabled={scholarLoading || !scholarInput.trim()}
              style={{
                border: "none",
                borderRadius: "50%",
                background: scholarInput.trim() && !scholarLoading ? "var(--gold)" : "rgba(128,128,128,0.1)",
                color: scholarInput.trim() && !scholarLoading ? "#fff" : "rgba(128,128,128,0.4)",
                width: "36px",
                height: "36px",
                cursor: scholarInput.trim() && !scholarLoading ? "pointer" : "default",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.25s",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </form>
        </aside>
      )}

      {showNotesPanel && (
        <aside style={{
          position: "fixed",
          top: "55px",
          right: 0,
          bottom: 0,
          width: "min(380px, 94vw)",
          background: S.panelBg,
          borderLeft: `1px solid ${S.panelBorder}`,
          boxShadow: `-12px 0 34px ${S.shadow}`,
          zIndex: 110,
          display: "flex",
          flexDirection: "column",
          color: S.text,
          transition: "background 0.3s ease, color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease",
        }}>
          <div style={{
            padding: "1rem",
            borderBottom: `1px solid ${S.panelBorder}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "1rem",
          }}>
            <div>
              <div style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "1.25rem",
                fontWeight: 700,
              }}>
                Highlights & Notes
              </div>
              <div style={{ fontSize: "0.75rem", opacity: 0.65, marginTop: "2px" }}>
                {annotations.length} saved for this book
              </div>
            </div>
            <button
              onClick={() => setShowNotesPanel(false)}
              style={{
                border: `1px solid ${S.panelBorder}`,
                background: "transparent",
                color: S.text,
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>

          {pendingNote && (
            <div style={{
              padding: "1rem",
              borderBottom: `1px solid ${S.panelBorder}`,
              background: theme === "dark" ? "rgba(79, 190, 183, 0.08)" : "rgba(43, 111, 151, 0.08)",
            }}>
              <div style={{ fontSize: "0.72rem", color: S.noteText, fontWeight: 700, marginBottom: "0.5rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                New note on page {pendingNote.page}
              </div>
              <blockquote style={{
                margin: "0 0 0.75rem",
                paddingLeft: "0.75rem",
                borderLeft: `3px solid ${S.noteRing}`,
                fontSize: "0.82rem",
                lineHeight: 1.45,
                opacity: 0.82,
              }}>
                {pendingNote.text.length > 180 ? `${pendingNote.text.slice(0, 180)}...` : pendingNote.text}
              </blockquote>
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Write your note..."
                autoFocus
                style={{
                  width: "100%",
                  minHeight: "88px",
                  resize: "vertical",
                  border: `1px solid ${S.panelBorder}`,
                  borderRadius: "8px",
                  padding: "10px",
                  background: S.bg,
                  color: S.text,
                  fontFamily: "'Outfit', sans-serif",
                  fontSize: "0.85rem",
                  lineHeight: 1.5,
                  outline: "none",
                  transition: "background 0.3s ease, color 0.3s ease, border-color 0.3s ease",
                }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "0.75rem" }}>
                <button
                  onClick={() => {
                    setPendingNote(null);
                    setNoteDraft("");
                  }}
                  style={panelBtnStyle(S)}
                >
                  Cancel
                </button>
                <button
                  onClick={() => saveAnnotation("note", pendingNote, noteDraft)}
                  disabled={!noteDraft.trim()}
                  style={{
                    ...panelBtnStyle(S, true),
                    opacity: noteDraft.trim() ? 1 : 0.45,
                    cursor: noteDraft.trim() ? "pointer" : "not-allowed",
                  }}
                >
                  Save Note
                </button>
              </div>
            </div>
          )}

          <div style={{
            flex: 1,
            overflowY: "auto",
            padding: "1rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
          }}>
            {annotations.length === 0 ? (
              <div style={{
                border: `1px dashed ${S.panelBorder}`,
                borderRadius: "10px",
                padding: "1.25rem",
                textAlign: "center",
                color: S.text,
                opacity: 0.72,
                fontSize: "0.86rem",
                lineHeight: 1.6,
              }}>
                Select text on the page to save your first highlight or note.
              </div>
            ) : annotations.map((item) => {
              const isNote = item.type === "note";
              return (
                <article
                  key={item.id}
                  style={{
                    background: S.bg,
                    border: `1px solid ${isNote ? S.noteRing : S.savedHighlightRing}`,
                    borderLeft: `4px solid ${isNote ? S.noteRing : S.savedHighlightRing}`,
                    borderRadius: "8px",
                    padding: "0.85rem",
                    boxShadow: `0 4px 14px ${S.shadow}`,
                    transition: "background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", marginBottom: "0.55rem" }}>
                    <span style={{
                      fontSize: "0.68rem",
                      color: isNote ? S.noteText : S.accent,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      fontWeight: 800,
                    }}>
                      {isNote ? "Note" : "Highlight"} · p. {item.page}
                    </span>
                    <button
                      onClick={() => deleteAnnotation(item.id)}
                      title="Delete"
                      style={{
                        border: "none",
                        background: "transparent",
                        color: S.text,
                        opacity: 0.45,
                        cursor: "pointer",
                        fontSize: "0.9rem",
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <p style={{
                    fontSize: "0.84rem",
                    lineHeight: 1.55,
                    margin: "0 0 0.65rem",
                    background: isNote ? S.noteBg : S.savedHighlightBg,
                    borderRadius: "7px",
                    padding: "0.6rem",
                  }}>
                    {item.text}
                  </p>
                  {item.note && (
                    <p style={{
                      fontSize: "0.82rem",
                      lineHeight: 1.5,
                      margin: "0 0 0.75rem",
                      padding: "0.65rem",
                      borderRadius: "7px",
                      background: theme === "dark" ? "rgba(10, 18, 32, 0.58)" : "#fff",
                      border: `1px solid ${S.noteRing}`,
                    }}>
                      {item.note}
                    </p>
                  )}
                  <button
                    onClick={() => jumpToAnnotation(item)}
                    style={panelBtnStyle(S)}
                  >
                    Jump to passage
                  </button>
                </article>
              );
            })}
          </div>
        </aside>
      )}



      {/* Achievement Unlocked modal */}
      <BadgeUnlockModal
        newBadge={newBadge}
        setNewBadge={setNewBadge}
      />

      {/* AudioPlayerWidget removed — TTS controls now live in the ReaderToolbar sub-bar */}

      {/* Keyframes and textLayer styling */}
      <style>{`
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes fadeIn  { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:none; } }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes slideUpAudio {
          from { transform: translateX(-50%) translateY(20px); opacity: 0; }
          to { transform: translateX(-50%) translateY(0); opacity: 1; }
        }
        @keyframes slideLeft {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
        @keyframes slideUpBottom {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        
        /* PDF.js Text Layer overrides */
        .textLayer {
          opacity: 1;
          mix-blend-mode: ${theme === "dark" ? "screen" : "multiply"};
        }
        .textLayer span {
          color: transparent;
          cursor: text;
        }
        .textLayer[data-speaking="true"] span {
          cursor: pointer !important;
        }
        .textLayer span:hover {
          background: ${S.hover} !important;
          border-radius: 4px;
        }
        /* ::selection override is injected into <head> via useEffect */
      `}</style>
    </div>
  );
}
